import React from 'react';
import NannieLogoSvg from '@/assets/icons/brand/nannielogo.svg';
import { GRADIENTS, applyGradientText } from '@/components/ui/styles/gradients';

// ===================================
// BRANDING COMPONENTS
// ===================================

// Common keyboard event handler
const createKeyDownHandler = (onClick?: () => void) => {
  return (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };
};

interface NannieLogoProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
}

export const NannieLogo: React.FC<NannieLogoProps> = ({ 
  size = 'md', 
  className = '',
  onClick
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8 sm:w-10 sm:h-10',
    lg: 'w-12 h-12'
  };

  const textSizes = {
    sm: 'text-lg',
    md: 'text-xl sm:text-2xl',
    lg: 'text-2xl sm:text-3xl'
  };

  const baseClasses = `flex items-center ${className}`;
  const handleKeyDown = createKeyDownHandler(onClick);

  if (onClick) {
    return (
      <button 
        className={`${baseClasses} cursor-pointer bg-transparent border-none p-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded`}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        aria-label="Nannie Logo"
      >
        <img 
          src={NannieLogoSvg} 
          className={sizeClasses[size]} 
          alt="Nannie Logo" 
        />
        <span className={`${textSizes[size]} font-bold font-museo text-gray-900`}>
          Nannie
        </span>
      </button>
    );
  }

  return (
    <div className={baseClasses}>
      <img 
        src={NannieLogoSvg} 
        className={sizeClasses[size]} 
        alt="Nannie Logo" 
      />
      <span className={`${textSizes[size]} font-bold font-museo text-gray-900`}>
        Nannie
      </span>
    </div>
  );
};

interface PicassoTitleProps {
  text?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  gradient?: keyof typeof GRADIENTS;
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export const PicassoTitle: React.FC<PicassoTitleProps> = ({ 
  text = 'Picasso',
  size = 'lg',
  gradient = 'picassoPrimary',
  className = '',
  onClick,
  style = {}
}) => {
  const sizeClasses = {
    sm: 'text-xl sm:text-2xl',
    md: 'text-2xl sm:text-3xl',
    lg: 'text-3xl sm:text-4xl md:text-5xl',
    xl: 'text-4xl sm:text-5xl md:text-6xl'
  };

  const baseClasses = `font-semibold font-museo leading-none tracking-normal`;
  
  const gradientStyle = applyGradientText(GRADIENTS[gradient]);
  const handleKeyDown = createKeyDownHandler(onClick);

  if (onClick) {
    return (
      <button
        className={`${baseClasses} ${sizeClasses[size]} ${className} cursor-pointer bg-transparent border-none p-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded`}
        style={{
          ...gradientStyle,
          ...style
        }}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        aria-label={`${text} title`}
      >
        {text}
      </button>
    );
  }

  return (
    <h1 
      className={`${baseClasses} ${sizeClasses[size]} ${className}`}
      style={{
        ...gradientStyle,
        ...style
      }}
    >
      {text}
    </h1>
  );
};

// ===================================
// DECORATIVE COMPONENTS
// ===================================

interface DecorativeLineProps {
  className?: string;
  showCircle?: boolean;
}

export const DecorativeLine: React.FC<DecorativeLineProps> = ({ 
  className = '',
  showCircle = true
}) => {
  return (
    <div className={`flex items-center justify-center mb-10 ${className}`}>
      <div className={`flex-1 h-px bg-gradient-to-r from-transparent via-[#F39A81] to-[#F39A81] max-w-[250px]`}></div>

      {showCircle && (
        <div
          className="mx-4 sm:mx-6 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-transparent"
          style={{
            background:
              "linear-gradient(white, white) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box",
          }}
        ></div>
      )}

      <div className={`flex-1 h-px bg-gradient-to-r from-[#F39A81] to-transparent max-w-[250px]`}></div>
    </div>
  );
};
